/**
 * Lazy loading of trajectories and body tables for a fleet (Sprint 3, S3-03, plan §5.2).
 *
 * - Start: only the trajectories active at the start date (and the tables they require) are loaded before the first
 *   frame; the loading screen waits for nothing else.
 * - Then one file at a time in idle time: requested ones first (selected or followed spacecraft), then by distance
 *   in time from `currentDate`; tables (Titan, Arrokoth) by the distance to their coverage windows. Nothing that has
 *   started is aborted.
 * - Fetching, parsing and decoding happen in a worker (no frame > 50 ms from JSON parsing); without workers (tests)
 *   on the main thread.
 */
import { writable } from 'svelte/store';
import { BodyTable, type DecodedTable } from '../core/bodyEphemeris';
import type { BodyData, EphemerisFile, MissionsFile, TrajectoryData, TrajectoryFile } from '../core/data';
import { type MoonBand, Trajectory } from '../core/trajectory';
import { decodeTrajectory } from '../core/trajectoryCodec';
import { tdbDaysToUtcMs } from '../core/time';
import type { LoadRequest, LoadResult } from './loadWorker';

export type LoadStatus = 'queued' | 'loading' | 'loaded' | 'failed';

/** Load status per spacecraft and table id (the detail panel shows "loading trajectory…"). */
export const loadStatus = writable<Record<string, LoadStatus>>({});

interface Item {
  id: string;
  kind: 'trajectory' | 'table';
  url: string;
  /** Time windows the item matters for, UTC ms. */
  windows: [number, number][];
  /** Tables that must be installed before this trajectory (encounter segments centred on them). */
  requires: string[];
  status: LoadStatus;
}

export interface LoaderSinks {
  trajectory(id: string, data: TrajectoryData, lineTimes: Float64Array): void;
  table(id: string, table: BodyTable): void;
  /** Bands for the line subdivision of trajectories (World.moonBands), by centre body. */
  bands(): Record<string, MoonBand[]>;
}

export class Loader {
  private readonly items = new Map<string, Item>();
  private readonly requested: string[] = [];
  private worker: Worker | null = null;
  private readonly pending = new Map<string, { resolve: (r: LoadResult) => void }>();
  private busy = false;
  private started = false;
  private sinks: LoaderSinks | null = null;
  private readonly held = new Map<string, { data: TrajectoryData; lineTimes: Float64Array }>();

  constructor(
    missions: MissionsFile,
    bodies: readonly BodyData[],
    private readonly base: string,
    private readonly now: () => number,
  ) {
    for (const m of missions.missions) {
      for (const sc of m.spacecraft) {
        const t = sc.trajectory;
        if (!t.file || !t.coverage) continue;
        this.items.set(sc.id, {
          id: sc.id,
          kind: 'trajectory',
          url: base + t.file,
          windows: [[tdbDaysToUtcMs(t.coverage[0]), tdbDaysToUtcMs(t.coverage[1])]],
          requires: t.requires ?? [],
          status: 'queued',
        });
      }
    }
    for (const b of bodies) {
      if (!b.table) continue;
      this.items.set(b.id, {
        id: b.id,
        kind: 'table',
        url: base + b.table.file,
        windows: b.table.windows.map(([a, c]) => [tdbDaysToUtcMs(a), tdbDaysToUtcMs(c)] as [number, number]),
        requires: [],
        status: 'queued',
      });
    }
    this.publish();
  }

  /** Items active at `ms` (inside one of their windows). */
  activeAt(ms: number): string[] {
    return [...this.items.values()].filter((i) => i.windows.some(([a, b]) => ms >= a && ms <= b)).map((i) => i.id);
  }

  /** Loads these items (and the tables they require) now, in parallel; resolves when all are installed. */
  async load(ids: readonly string[], sinks: LoaderSinks): Promise<void> {
    this.sinks = sinks;
    const all = new Set<string>();
    for (const id of ids) {
      const item = this.items.get(id);
      if (!item) continue;
      for (const r of item.requires) all.add(r);
      all.add(id);
    }
    await Promise.all([...all].map((id) => this.fetchItem(id)));
  }

  /**
   * Starts loading the rest in idle time, one file at a time, after `delayMs` of quiet (the first seconds belong to
   * the start-up; requested items do not wait).
   */
  start(sinks: LoaderSinks, delayMs = 2500): void {
    this.sinks = sinks;
    this.started = true;
    this.notBefore = performance.now() + delayMs;
    this.schedule();
  }

  private notBefore = 0;
  private waiting = false;

  /** Moves an item (a spacecraft, with what it requires) to the front of the queue. */
  request(id: string): void {
    const item = this.items.get(id);
    if (!item || item.status === 'loaded' || item.status === 'loading') return;
    for (const r of [...item.requires, id]) if (!this.requested.includes(r)) this.requested.push(r);
    this.schedule();
  }

  /** Pending items in the order they would load now (debugState). */
  queue(): string[] {
    const pending = [...this.items.values()].filter((i) => i.status === 'queued' || i.status === 'failed');
    const now = this.now();
    const distance = (i: Item): number =>
      Math.min(...i.windows.map(([a, b]) => (now < a ? a - now : now > b ? now - b : 0)));
    return pending
      .sort((a, b) => {
        const ra = this.requested.indexOf(a.id);
        const rb = this.requested.indexOf(b.id);
        if (ra !== rb) return (ra < 0 ? Infinity : ra) - (rb < 0 ? Infinity : rb);
        return distance(a) - distance(b);
      })
      .map((i) => i.id);
  }

  status(id: string): LoadStatus | undefined {
    return this.items.get(id)?.status;
  }

  private schedule(): void {
    if (!this.started || this.busy) return;
    const next = this.queue().find((id) => this.items.get(id)!.status === 'queued');
    if (!next) return;
    const wait = this.notBefore - performance.now();
    if (!this.requested.includes(next) && wait > 0) {
      if (!this.waiting) {
        this.waiting = true;
        setTimeout(() => {
          this.waiting = false;
          this.schedule();
        }, wait);
      }
      return;
    }
    this.busy = true;
    const run = (): void => {
      void this.fetchItem(next).finally(() => {
        this.busy = false;
        this.schedule();
      });
    };
    // requested items go at once; the rest waits for idle time (never in the middle of busy frames)
    if (this.requested.includes(next)) run();
    else if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 50);
  }

  private async fetchItem(id: string): Promise<void> {
    const item = this.items.get(id);
    if (!item || item.status === 'loaded' || item.status === 'loading') return;
    item.status = 'loading';
    this.publish();
    try {
      const result = await this.run(
        item.kind === 'trajectory'
          ? { kind: 'trajectory', id, url: item.url, bands: this.sinks!.bands() }
          : { kind: 'table', id, url: item.url },
      );
      if (result.kind === 'error') throw new Error(result.message);
      if (result.kind === 'table') {
        this.sinks!.table(id, new BodyTable(result.table));
        item.status = 'loaded';
        this.releaseHeld();
      } else if (result.kind === 'trajectory') {
        item.status = 'loaded';
        this.held.set(id, { data: result.data, lineTimes: result.lineTimes });
        this.releaseHeld();
      }
    } catch (err) {
      item.status = 'failed';
      console.error(`Loading ${id} failed:`, err);
    }
    const r = this.requested.indexOf(id);
    if (r >= 0) this.requested.splice(r, 1);
    this.publish();
  }

  /** Installs loaded trajectories whose required tables are installed too. */
  private releaseHeld(): void {
    for (const [id, h] of this.held) {
      const item = this.items.get(id)!;
      if (item.requires.some((r) => this.items.get(r)?.status !== 'loaded')) {
        for (const r of item.requires) this.request(r);
        continue;
      }
      this.held.delete(id);
      this.sinks!.trajectory(id, h.data, h.lineTimes);
    }
  }

  private run(req: LoadRequest): Promise<LoadResult> {
    if (typeof Worker === 'undefined') return runInline(req);
    if (!this.worker) {
      this.worker = new Worker(new URL('./loadWorker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<LoadResult>): void => {
        const p = this.pending.get(e.data.id);
        this.pending.delete(e.data.id);
        p?.resolve(e.data);
      };
    }
    return new Promise((resolve) => {
      this.pending.set(req.id, { resolve });
      this.worker!.postMessage(req);
    });
  }

  private publish(): void {
    const status: Record<string, LoadStatus> = {};
    for (const [id, i] of this.items) status[id] = i.status;
    loadStatus.set(status);
  }

  dispose(): void {
    this.worker?.terminate();
    this.started = false;
  }
}

/** The worker's job on the main thread (no Worker available). */
async function runInline(req: LoadRequest): Promise<LoadResult> {
  const res = await fetch(req.url);
  if (!res.ok) return { kind: 'error', id: req.id, message: `HTTP ${res.status}` };
  if (req.kind === 'trajectory') {
    const data = decodeTrajectory((await res.json()) as TrajectoryFile);
    return {
      kind: 'trajectory',
      id: req.id,
      data,
      lineTimes: new Trajectory(data).sampleTimes((c) => req.bands[c] ?? []),
    };
  }
  const table: DecodedTable = BodyTable.decode((await res.json()) as EphemerisFile);
  return { kind: 'table', id: req.id, table };
}
